import { SocketCodes } from './../../../utils/constants';
import { ITable, PlayerOptions } from './../../game/model/interfaces';
import { BasePreferences } from './../../preferences/model/types';
import { Message } from '@base/features/chat/message';
import { IPlayer, IPlayersOnlineRepository, LastInOutPlayer, PlayerStorageKeys } from './interfaces';
import { Subject, BehaviorSubject } from 'rxjs';
import { ISocketClient } from '@base/features/socket/interfaces';
import SoundProvider from '@base/features/game/model/soundProvider';
import Player from '@base/features/game/model/player';
import BufferReader from '@base/features/socket/bufferReader';
import Codes from '@base/features/socket/codes';
import Utils from '@base/features/socket/utils';
import BaseTable from '@base/features/game/model/baseTable';
import { ILocalStorage } from '@base/features/storage/interfaces';

export default class PlayersOnlineRepository<
  P extends BasePreferences = BasePreferences,
  T extends ITable = BaseTable
> implements IPlayersOnlineRepository<P, T> {
  private players = new Map<number, IPlayer>();
  me?: IPlayer;
  onInOutPlayer = new Subject<LastInOutPlayer>();
  onPlayersListChanged = new Subject<void>();
  onPlayerLeft = new Subject<any>();
  onLoginReady = new BehaviorSubject(false);

  constructor(private socket: ISocketClient, private soundProvider: SoundProvider<P>, private localStorage: ILocalStorage) {}

  clear(): void {
    this.players.clear();
    this.onPlayersListChanged.next()
  }

  get sound() {
    return this.soundProvider;
  }

  private addSocketPlayer(data: IPlayer): IPlayer {
    //У нас может не быть pid? В каких случаях?
    if (!data || data.pid === undefined || data.pid < 0) {
      throw new Error('DataProvider: invalid player from network for adding');
    }

    let player = this.getPlayerOnline(data.pid);
    if (!player) {
      player = new Player();
      player.id = data.pid; // в данном случае id дублирует pid
    }
    Object.assign(player, data);
    this.addPlayer(player);
    return player;
  }

  private onReceiveFinger(data: BufferReader) {
    const pid = data.readInt16();
    const countryCode = data.readUTFWithLength();
    const country = data.readUTFWithLength();
    const player = this.getPlayerOnline(pid);
    if (!player) throw new Error(`PlayersOnlineRepository: player ${pid} doesn't exist`);
    player.country = country;
    player.countryCode = countryCode;
  }

  private onReceiveYouAre(data: BufferReader) {
    const pid = data.readUint16();
    const rcLeft = data.readInt32();
    const player = this.addSocketPlayer(({ pid, rcLeft } as unknown) as IPlayer);
    this.sendFingerPid(pid);
    if (!player) throw new Error('DataProvider: invalid player for setIAm');
    this.me = player;
  }

  private onReceiveLeave(data: BufferReader) {
    const pid = data.readInt16();
    //const reason = data.readUint8();
    const player = this.getPlayerOnline(pid);
    this.onPlayersListChanged.next();
    if (player?.pid) {
      const name = this.players.get(pid)?.name;
      if (!name) return;
      this.players.delete(pid);
      this.onInOutPlayer.next({ name, status: 'out' });
    }
  }

  private onReceiveAvatar(data: BufferReader) {
    const size = data.readUint16();
    const dataAvatar = data.read(size).toUint8Array();
    const avatar = {
      pid: data.readInt16(),
      type: data.readUint8(),
      size: size,
      data: dataAvatar,
    };
    this.updatePlayerAvatar(avatar);
  }

  private onReceiveAlive(data: BufferReader) {
    const mode = data.readInt8();
    const pid = data.readInt16();
    const player = this.getPlayerOnline(pid);

    if (!player)
      return console.error('Cannot parse alive message for player: ' + pid + ' reason: player not found');

    if (mode === Codes.AliveMode.ALV_NORMAL) {
      if (player.id === this.me?.id) {
        this.sendAlive(Codes.AliveMode.ALV_NORMAL);
        player.away = false;
      }
    } else if (mode === Codes.AliveMode.ALV_SLEEP || mode === Codes.AliveMode.ALV_WAKEUP) {
      player.away = mode === Codes.AliveMode.ALV_SLEEP;
      // if (Main.appLoaded) {
      //   this.emitDeferredEvent(this.events.players_list_changed);
      // }
    } else if (mode === Codes.AliveMode.ALV_BOT) {
      // player.bot = true;
    }
  }

  private onReceiveStat(data: BufferReader) {
    const stat: PlayerOptions = {
      pid: data.readInt16(),
      data: {
        userId: data.readInt64S(),
        status: data.readInt32(),
        lastLogin: data.readInt32(),
        onlineTime: data.readInt32(),
        realLevel: data.readInt32(),
        registration: data.readInt32(),
        nGames: data.readInt32(),
        nWin: data.readInt32(),
        nLost: data.readInt32(),
        nDraw: data.readInt32(),
        avgOppRating: data.readInt32(),
        rating: data.readInt32(),
        maxRating: data.readInt32(),
        nMt: data.readInt32(),
        nMtWin: data.readInt32(),
        nTr: data.readInt32(),
        nTr1: data.readInt32(),
        nTr2: data.readInt32(),
        nTr3: data.readInt32(),
        ladderPos: data.readInt32(),
      },
    };
    const error = this.checkParams(stat);
    if (error) {
      console.log('Error: updatePlayerStat', error, stat);
      return;
    }

    const player = this.getPlayerOnline(stat.pid);
    if (!player) return;
    Object.assign(player, stat.data);
    this.addPlayer(player);
    player.pid === this.me?.pid && this.localStorage.set(PlayerStorageKeys.GAMES_PLAYED, player.getAdditionalInfo().totalGames);
  }

  private onReceiveJoin(data: BufferReader) {
    const pid = data.readUint16();
    const name = data.readUTFWithLength();
    const rating = data.readUint16();
    const status = data.readInt32();
    const join = data.readBoolean();
    const away = data.readBoolean();
    const avatar = data.readUint16();
    const gender = 0;

    this.onPlayerJoined({
      pid,
      name,
      rating,
      status,
      join,
      away: away,
      avatar: avatar,
      gender: gender,
    } as any);
  }

  sendFingerPid(pid: number): void {
    const data = [Codes.Types.CP_FINGER].concat(Utils.decToInt16(pid));
    this.socket.sendPacket(data);
  }

  private sendAlive(mode: number): void {
    const data = [SocketCodes.CP_ALIVE].concat(Utils.decToInt8(mode));
    this.socket.sendPacket(data);
  }

  onPlayerAwayChanged(isAway: boolean): void {
    this.sendAlive(isAway ? Codes.AliveMode.ALV_SLEEP : Codes.AliveMode.ALV_WAKEUP);
  }

  //Событие - пользователь зашел в onLine + запрашиваем прочие данные по пользователю.
  public onPlayerJoined(data: IPlayer): void {
    const newPlayer = this.addSocketPlayer(data);
    //запрашиваем доп данные только для игроков с кастомными аватарами, иначе сильная нагрузка
    // if (newPlayer.avatar <= 0 || newPlayer.avatar > 31) {
      this.sendFingerPid(newPlayer.pid);
    // }
    //остальных отложил, но можно и совсем убрать, а в приложении по каждому запрашивать, когда нужно (в PlayerAdditionalInfo, например)
    //но в таком случае, мы создаем лишнюю нагрузку на приложение и сервер, тк ити данные могут и не понадобиться никогда
    // else {
    //   setTimeout(() => {
    //     this.sendFingerPid(newPlayer.pid);
    //   }, 12000);
    // }

    this.onPlayersListChanged.next();
  }

  findPlayer(players: IPlayer[], id: number): IPlayer | undefined {
    return players.find((player) => player.getPid() === id);
  }

  checkParams(params: any, options?: any): string | undefined {
    if (typeof params.pid != 'undefined' && options && params.pid !== options.allowPlayerId) {
      if (params.pid < 0 && !options.allowNegativePlayerId) {
        return 'invalid player id';
      }

      if (!this.getPlayerOnline(params.pid)) {
        return "player doesn't exist";
      }

      if (options.me && this.getPlayerOnline(params.pid) !== this.me) {
        return 'player should be me';
      }
    }
  }

  updatePlayerAvatar(avatar: Record<string, unknown>) {
    const error = this.checkParams(avatar);
    if (error) {
      console.info('Error: updatePlayerAvatar', error, avatar);
      return;
    }

    // let b = new Blob(avatar.data.buffer, {type : 'image/gif'});
    // let b = new Blob(avatar.data.buffer);
    // console.log('adsds', avatar.data.buffer);
    // saveAs(b, avatar.pid + ".gif");

    // encode bitmap?!
    // Codes.AvatarSize.names
  }

  addPlayer(player: IPlayer) {
    this.players.set(player.pid, player);
    this.onInOutPlayer.next({ name: player.name, status: 'in' });
  }

  getPlayersOnline() {
    return this.players;
  }

  getPlayerOnline(pid = -1) {
    return this.players.get(pid);
  }

  //FIXME: Этот метод должен быть в реализации чатов, а не в репозитории онлайн игроков
  addMessage(pid: number, message: Message) {
    const player = this.players.get(pid);
    if (!player) return;
    player.addChatMessage(message);
  }
}
